/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import type { EuiTableComputedColumnType } from '@elastic/eui'
import { EuiBasicTable, EuiFlexGroup, EuiFlexItem } from '@elastic/eui'

import type { CCMRole } from '@modules/ccm-user-components/rbac/types'
import { FormattedMessage } from '@modules/i18n'
import { ccmClusterRoleLabels } from '@modules/role-assignments-lib/messages'
import { resourceRoleLabelByRoleId } from '@modules/role-assignments-lib/types'

import type { FunctionComponent } from 'react'
import type { MessageDescriptor } from 'react-intl'

export type AllCCMRoleAssignmentsTableItem = {
  roleIds: CCMRole[]
}

const AllCCMRoleAssignmentsTable: FunctionComponent<{
  items: AllCCMRoleAssignmentsTableItem[]
}> = ({ items }) => (
  <EuiBasicTable<AllCCMRoleAssignmentsTableItem>
    data-test-id='all-role-assignments-table'
    items={items}
    columns={getColumns()}
  />
)

const getColumns = (): Array<EuiTableComputedColumnType<AllCCMRoleAssignmentsTableItem>> => [
  {
    name: (
      <FormattedMessage
        id='roles-assignments.cloud-connected.allAssignmentRoleColumn'
        defaultMessage='Role'
      />
    ),
    render: (tableItem) => {
      const rolesToProcess = tableItem.roleIds
      const roleItems = rolesToProcess.map((roleId) => {
        const roleLabel = resourceRoleLabelByRoleId[roleId]
        const predefinedLabels: Record<string, MessageDescriptor> = ccmClusterRoleLabels

        return (
          <EuiFlexItem grow={false} key={`connected-cluster${roleId}`}>
            <FormattedMessage {...predefinedLabels[roleLabel]} />
          </EuiFlexItem>
        )
      })
      return (
        <EuiFlexGroup direction='column' justifyContent='flexStart' gutterSize='xs'>
          {roleItems}
        </EuiFlexGroup>
      )
    },
    width: '45%',
  },
]

export default AllCCMRoleAssignmentsTable
